import { useBillingActions, useBillingSummary, type BillingSummary, type CheckoutVariant } from '@/hook';
import { cn } from '@/lib/cn';
import { formatUsd } from '@/lib/money';
import { Button } from './Button';
import { LoadingState } from './LoadingState';
import { Modal } from './Modal';
import { MonoLabel } from './MonoLabel';
import { checkoutSheet } from './checkoutSheet';

interface CheckoutModalProps {
  variant: CheckoutVariant | null;
  onClose: () => void;
}

const TONE = { ink: 'text-ink', mute: 'text-mute', up: 'text-up', down: 'text-down' } as const;

/** The checkout, one sheet per variant. Remounts per variant so nothing
 * carries over between them. */
export function CheckoutModal({ variant, onClose }: CheckoutModalProps) {
  if (!variant) return null;
  return <CheckoutSheet key={variant.kind} variant={variant} onClose={onClose} />;
}

function CheckoutSheet({ variant, onClose }: { variant: CheckoutVariant; onClose: () => void }) {
  const summary = useBillingSummary();
  const { confirm, openPortal, pending, error } = useBillingActions();

  if (!summary) {
    return (
      <Modal open onClose={onClose} title="Checkout" maxWidth={620}>
        <LoadingState />
      </Modal>
    );
  }

  const sheet = checkoutSheet(variant, summary);

  async function submit() {
    const ok = await confirm(variant);
    if (ok && !sheet.redirects) onClose();
  }

  return (
    <Modal open onClose={onClose} title={sheet.title} kicker={sheet.kicker} maxWidth={620}>
      <div className="mt-[26px]">
        {sheet.lines.map((line) => (
          <div key={line.k} className="flex items-baseline justify-between gap-4 border-b border-stripeA py-[13px]">
            <span className={cn('text-sm', TONE[line.tone])}>{line.k}</span>
            <span className={cn('font-mono text-sm tabular-nums', TONE[line.tone], line.strong && 'font-medium')}>{line.v}</span>
          </div>
        ))}
      </div>
      <div className="flex items-baseline justify-between gap-4 pt-[18px]">
        <span className="text-base font-semibold">Due today</span>
        <span className="text-2xl font-semibold tracking-[-0.03em]">{formatUsd(sheet.dueCents)}</span>
      </div>
      <div className="mt-[26px]">
        <MonoLabel size="md" className="block border-b border-line pb-[10px]">
          Paying with
        </MonoLabel>
        <PayingWith method={summary.paymentMethod} redirects={sheet.redirects} onChange={() => void openPortal()} />
      </div>
      <Button className="mt-3 w-full py-[14px] text-[15px]" disabled={pending} onClick={() => void submit()}>
        {sheet.cta}
      </Button>
      {error && <p className="mt-3 text-[13px] text-down">{error}</p>}
      <p className="mt-4 text-xs leading-[1.6] text-faint">
        {sheet.fine}
        {sheet.termsUrl && (
          <>
            {' '}
            <a href={sheet.termsUrl} target="_blank" rel="noreferrer" className="text-mute underline hover:text-ink">
              Terms
            </a>
          </>
        )}
      </p>
    </Modal>
  );
}

/** The card on file, or where one will be asked for. */
function PayingWith({
  method,
  redirects,
  onChange,
}: {
  method: BillingSummary['paymentMethod'];
  redirects: boolean;
  onChange: () => void;
}) {
  return (
    <div className="flex items-center gap-4 py-[18px]">
      <div className="h-[30px] w-[46px] shrink-0 rounded border border-line" aria-hidden="true" />
      <div className="flex-1">
        {method ? (
          <>
            <div className="font-mono text-sm">•••• {method.last4}</div>
            <div className="mt-[2px] text-[13px] text-mute">
              Expires {String(method.expMonth).padStart(2, '0')} / {String(method.expYear).slice(-2)}
            </div>
          </>
        ) : (
          <>
            <div className="text-sm">No card on file</div>
            <div className="mt-[2px] text-[13px] text-mute">{redirects ? "Entered on Stripe's secure page." : 'Add one from the plan tab first.'}</div>
          </>
        )}
      </div>
      {method && (
        <Button variant="outline" size="sm" className="px-[13px] py-2" onClick={onChange}>
          Change
        </Button>
      )}
    </div>
  );
}
